import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './LocalizeTab.css';

const API = process.env.REACT_APP_API_URL || '';

const ALL_LANGS = [
  { code: 'FR', name: 'French',     flag: '\uD83C\uDDEB\uD83C\uDDF7' },
  { code: 'ES', name: 'Spanish',    flag: '\uD83C\uDDEA\uD83C\uDDF8' },
  { code: 'PT', name: 'Portuguese', flag: '\uD83C\uDDE7\uD83C\uDDF7' },
  { code: 'DE', name: 'German',     flag: '\uD83C\uDDE9\uD83C\uDDEA' },
  { code: 'IT', name: 'Italian',    flag: '\uD83C\uDDEE\uD83C\uDDF9' },
  { code: 'KO', name: 'Korean',     flag: '\uD83C\uDDF0\uD83C\uDDF7' },
  { code: 'JA', name: 'Japanese',   flag: '\uD83C\uDDEF\uD83C\uDDF5' },
  { code: 'ZH', name: 'Chinese',    flag: '\uD83C\uDDE8\uD83C\uDDF3' },
  { code: 'AR', name: 'Arabic',     flag: '\uD83C\uDDF8\uD83C\uDDE6' },
  { code: 'TR', name: 'Turkish',    flag: '\uD83C\uDDF9\uD83C\uDDF7' },
];

const CANVAS_W = 180;
const CANVAS_H = Math.round(CANVAS_W * 16 / 9);
const VIDEO_W  = 1080;
const VIDEO_H  = 1920;

function pickMusicByMood(bank, mood) {
  if (!bank.length) return null;
  const pool = bank.filter(m => m.mood === mood);
  const src = pool.length ? pool : bank;
  return src[Math.floor(Math.random() * src.length)]?.filename || null;
}

function defaultLangState(titleText) {
  const yPct = window._ocrTitleYPct || 18;
  return { titleText: titleText || '', titlePos: { x: 50, y: yPct }, patchPos: { x: 50, y: yPct }, titleSize: 48, titleTimeIn: 0, titleTimeOut: 5, hasTitle: true, script: '' };
}

export default function LocalizeTab({ apiBase, elApiKey, anthropicKey, selectedVoiceIds, voices }) {
  const base = apiBase || API;

  const [step, setStep]                   = useState(1);
  const [uploading, setUploading]         = useState(false);
  const [videoPreview, setVideoPreview]   = useState(null);
  const [jobId, setJobId]                 = useState(() => localStorage.getItem('loc_jobId') || null);
  const [transcript, setTranscript]       = useState('');
  const [sttError, setSttError]           = useState(null);
  const [ocrStatus, setOcrStatus]         = useState('idle');
  const [ocrTitle, setOcrTitle]           = useState('');
  const [titleTimeOutAutoDetected, setTitleTimeOutAutoDetected] = useState(false);
  const [selectedLangs, setSelectedLangs] = useState([]);
  const [dragOver, setDragOver]           = useState(false);
  const [langState, setLangState]         = useState({});
  const [translating, setTranslating]     = useState(false);

  const [titleTimeIn,  setTitleTimeIn]  = useState(0);
  const [titleTimeOut, setTitleTimeOut] = useState(5);
  const [videoDur, setVideoDur]         = useState(10);
  const timelineDragRef                 = useRef(null);
  const timelineRef                     = useRef(null);

  const [selVoice, setSelVoice]           = useState(null);
  const [locVoices, setLocVoices]         = useState({ female: [], male: [] });
  const [musicBank, setMusicBank]         = useState([]);
  const [musicFilename, setMusicFilename] = useState(null);
  const [musicMood, setMusicMood]         = useState('mid');
  const [musicBrowser, setMusicBrowser]   = useState(false);
  const [voVol, setVoVol]                 = useState(100);
  const [muVol, setMuVol]                 = useState(20);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewProg, setPreviewProg]     = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewAudioRef                   = useRef(null);

  const [exporting, setExporting]   = useState(false);
  const [langStatus, setLangStatus] = useState({});

  const fileRef      = useRef(null);
  const canvasRefs   = useRef({});
  const pollRef      = useRef(null);
  const previewTimer = useRef(null);

  useEffect(() => {
    axios.get(`${base}/api/music`).then(r => setMusicBank(r.data || [])).catch(() => {});
    axios.get(`${base}/api/localize/voices`).then(r => setLocVoices(r.data || { female: [], male: [] })).catch(() => {});
  }, [base]);

  useEffect(() => {
    if (musicBank.length && !musicFilename) setMusicFilename(pickMusicByMood(musicBank, musicMood));
  }, [musicBank]); // eslint-disable-line

  useEffect(() => {
    if (!selVoice && selectedVoiceIds?.length) setSelVoice(selectedVoiceIds[0]);
  }, [selectedVoiceIds]); // eslint-disable-line

  useEffect(() => {
    if (!jobId || !Object.keys(langStatus).length) return;
    const allDone = Object.values(langStatus).every(s => s.status === 'done' || s.status === 'error');
    if (allDone) { clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(async () => {
      try {
        const r    = await axios.get(`${base}/api/localize/status/${jobId}`);
        const data = r.data;
        const upd  = {};
        selectedLangs.forEach(c => { if (data[c]) upd[c] = data[c]; });
        if (Object.keys(upd).length) setLangStatus(prev => ({ ...prev, ...upd }));
        const now = { ...langStatus, ...upd };
        if (selectedLangs.every(c => now[c]?.status === 'done' || now[c]?.status === 'error') || data.allDone)
          clearInterval(pollRef.current);
      } catch {}
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [jobId, langStatus, selectedLangs, base]); // eslint-disable-line

  const detectMood = useCallback(async (text, bank) => {
    if (!text || !bank.length || !anthropicKey) return;
    try {
      const r = await axios.post('https://api.anthropic.com/v1/messages', {
        model: 'claude-haiku-4-5-20251001', max_tokens: 10,
        messages: [{ role: 'user', content: `Classify the energy of this ad script as exactly one word: high, mid, or low.\n\n${text}` }]
      }, { headers: { 'x-api-key': anthropicKey, 'anthropic-version': '2023-06-01', 'Content-Type': 'application/json' }, timeout: 10000 });
      const mood = (r.data.content[0]?.text || 'mid').trim().toLowerCase().replace(/[^a-z]/g, '');
      const m    = ['high', 'mid', 'low'].includes(mood) ? mood : 'mid';
      setMusicMood(m);
      const p = pickMusicByMood(bank, m); if (p) setMusicFilename(p);
    } catch {}
  }, [anthropicKey]);

  const runTranslate = useCallback(async (langs, tx, baseTitle) => {
    if (!langs.length || !tx || !anthropicKey) return;
    setTranslating(true);
    try {
      const r  = await axios.post(`${base}/api/localize/translate`, { jobId, transcript: tx, titleText: baseTitle, langs, anthropicKey });
      const tr = r.data.translations || {};
      setLangState(prev => {
        const next = { ...prev };
        langs.forEach(code => {
          if (!next[code]) next[code] = defaultLangState(tr[code]?.title || baseTitle);
          next[code].script    = tr[code]?.script || next[code].script;
          next[code].titleText = tr[code]?.title  || next[code].titleText;
        });
        return next;
      });
    } catch (err) { console.error('[localize] translate', err.message); }
    setTranslating(false);
  }, [base, jobId, anthropicKey]);

  const handleUpload = useCallback(async (file) => {
    if (!file || !elApiKey) return;
    setUploading(true); setSttError(null);
    setVideoPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append('video', file);
      fd.append('elApiKey', elApiKey);
      const r   = await axios.post(`${base}/api/localize/extract`, fd, { timeout: 180000 });
      const jid = r.data.jobId;
      setJobId(jid); localStorage.setItem('loc_jobId', jid);
      const tx = r.data.transcript || '';
      setTranscript(tx);
      if (r.data.sttError) setSttError(r.data.sttError);
      if (r.data.duration) setVideoDur(Math.ceil(r.data.duration));
      if (tx && musicBank.length) detectMood(tx, musicBank);
      if (anthropicKey) {
        setOcrStatus('scanning');
        try {
          const ocr = await axios.post(`${base}/api/localize/ocr`, { jobId: jid, anthropicKey, seek: 0.5 });
          const detectedTitle = (ocr.data.text || '').trim();
          if (detectedTitle) {
            setOcrTitle(detectedTitle); setOcrStatus('found');
            if (ocr.data.titleTopPct != null) { window._ocrTitleYPct = ocr.data.titleTopPct; window._ocrCaptionYPct = ocr.data.captionTopPct; }
            let titleEnd = null;
            for (const s of [1.5, 2.0, 2.5, 3.0, 4.0, 5.0]) {
              if (s >= (r.data.duration || 10)) break;
              try {
                const ocr2 = await axios.post(`${base}/api/localize/ocr`, { jobId: jid, anthropicKey, seek: s });
                if (!(ocr2.data.text || '').trim() || (ocr2.data.text || '').trim().length < detectedTitle.length * 0.4) { titleEnd = s; break; }
              } catch {}
            }
            if (titleEnd !== null) { setTitleTimeOut(titleEnd); setTitleTimeOutAutoDetected(true); }
          } else { setOcrStatus('empty'); }
        } catch { setOcrStatus('empty'); }
      }
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); setVideoPreview(null); }
    setUploading(false);
  }, [elApiKey, anthropicKey, base, musicBank, detectMood]);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0]; if (f) handleUpload(f);
  }, [handleUpload]);

  const goToStep2 = useCallback(() => {
    if (!selectedLangs.length || !jobId) return;
    setLangState(prev => {
      const next = { ...prev };
      selectedLangs.forEach(code => { if (!next[code]) next[code] = defaultLangState(ocrTitle); });
      return next;
    });
    if (transcript.trim()) runTranslate(selectedLangs, transcript, ocrTitle);
    setStep(2);
  }, [selectedLangs, jobId, transcript, ocrTitle, runTranslate]);

  const updateLang = useCallback((code, patch) => {
    setLangState(prev => ({ ...prev, [code]: { ...prev[code], ...patch } }));
  }, []);

  const videoFrameRef = useRef(null);
  const drawTitleOnCtx = useCallback((ctx, st) => {
    if (!st.hasTitle || !st.titleText) return;
    const fs  = Math.max(8, Math.round(st.titleSize * CANVAS_W / VIDEO_W));
    ctx.font = `900 ${fs}px 'Arial Black', Impact, sans-serif`;
    const tw  = ctx.measureText(st.titleText.toUpperCase()).width;
    const padX = Math.round(fs * 0.55), padY = Math.round(fs * 0.30);
    const bw  = Math.min(CANVAS_W - 8, tw + padX * 2), bh = fs + padY * 2;
    const bx  = (CANVAS_W - bw) / 2, by = CANVAS_H * (st.titlePos.y / 100) - bh / 2;
    const r   = Math.min(Math.round(fs * 0.22), bh / 2);
    ctx.fillStyle = '#fff'; ctx.beginPath();
    ctx.moveTo(bx + r, by); ctx.lineTo(bx + bw - r, by); ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + r);
    ctx.lineTo(bx + bw, by + bh - r); ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - r, by + bh);
    ctx.lineTo(bx + r, by + bh); ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - r);
    ctx.lineTo(bx, by + r); ctx.quadraticCurveTo(bx, by, bx + r, by);
    ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#000'; ctx.font = `900 ${fs}px 'Arial Black', Impact, sans-serif`;
    ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
    ctx.fillText(st.titleText.toUpperCase(), CANVAS_W / 2, by + bh / 2);
  }, []);

  const drawCanvas = useCallback((code) => {
    const st  = langState[code]; if (!st) return;
    const cvs = canvasRefs.current[code]; if (!cvs) return;
    const ctx = cvs.getContext('2d'); ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    if (videoFrameRef.current) { ctx.drawImage(videoFrameRef.current, 0, 0, CANVAS_W, CANVAS_H); }
    else { ctx.fillStyle = '#111'; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H); }
    drawTitleOnCtx(ctx, st);
  }, [langState, drawTitleOnCtx]);

  useEffect(() => {
    if (!videoPreview) return;
    const video = document.createElement('video'); video.crossOrigin = 'anonymous'; video.src = videoPreview; video.muted = true; video.currentTime = 0.5;
    video.addEventListener('seeked', () => {
      const ofs = document.createElement('canvas'); ofs.width = CANVAS_W; ofs.height = CANVAS_H;
      ofs.getContext('2d').drawImage(video, 0, 0, CANVAS_W, CANVAS_H);
      videoFrameRef.current = ofs;
      selectedLangs.forEach(code => drawCanvas(code));
    }, { once: true });
  }, [videoPreview]); // eslint-disable-line

  useEffect(() => { selectedLangs.forEach(code => drawCanvas(code)); }, [langState, selectedLangs, drawCanvas]);

  const getTimeFromEvent = useCallback((e) => {
    const tl = timelineRef.current; if (!tl) return null;
    const r  = tl.getBoundingClientRect();
    return Math.round(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * videoDur * 10) / 10;
  }, [videoDur]);

  const startTimelineDrag = useCallback((e, handle) => {
    e.preventDefault(); e.stopPropagation(); timelineDragRef.current = handle;
    const onMove = (ev) => {
      const t = Math.round(Math.max(0, Math.min(videoDur, (ev.clientX - (timelineRef.current?.getBoundingClientRect().left || 0)) / (timelineRef.current?.getBoundingClientRect().width || 1) * videoDur)) * 10) / 10;
      if (timelineDragRef.current === 'in')  setTitleTimeIn(prev => Math.min(t, titleTimeOut - 0.1));
      if (timelineDragRef.current === 'out') setTitleTimeOut(prev => Math.max(t, titleTimeIn  + 0.1));
    };
    const onUp = () => { timelineDragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  }, [videoDur, titleTimeIn, titleTimeOut]);

  const togglePreview = useCallback(async () => {
    if (previewPlaying) {
      if (previewAudioRef.current) { previewAudioRef.current.pause(); previewAudioRef.current = null; }
      clearInterval(previewTimer.current); setPreviewPlaying(false); setPreviewProg(0); return;
    }
    if (!selVoice || !elApiKey) return;
    const previewText = (Object.values(langState).find(s => s.script)?.script || transcript || '').slice(0, 200).trim();
    if (!previewText) return;
    setPreviewLoading(true);
    try {
      const r = await axios.post(`${base}/api/localize/preview`, { text: previewText, voiceId: selVoice, elApiKey, musicFilename, voiceVolume: voVol / 100, musicVolume: muVol / 100 });
      const audio = new Audio(`${base}/api/stream/${r.data.filename}`);
      audio.preload = 'auto'; previewAudioRef.current = audio;
      const startPb = () => {
        setPreviewPlaying(true); setPreviewProg(0);
        audio.play().catch(e => console.warn('[preview]', e));
        previewTimer.current = setInterval(() => {
          setPreviewProg(p => { if (p >= 1) { clearInterval(previewTimer.current); setPreviewPlaying(false); if (previewAudioRef.current) { previewAudioRef.current.pause(); previewAudioRef.current = null; } return 0; } return p + 1 / 50; });
        }, 200);
      };
      audio.addEventListener('canplay', startPb, { once: true });
      setTimeout(() => { if (!previewPlaying) startPb(); }, 800);
      audio.onended = () => { clearInterval(previewTimer.current); setPreviewPlaying(false); setPreviewProg(0); previewAudioRef.current = null; };
    } catch (err) { console.error('[preview]', err.message); }
    setPreviewLoading(false);
  }, [previewPlaying, selVoice, elApiKey, langState, transcript, musicFilename, voVol, muVol, base]);

  const renderTitlePng = useCallback((text, size) => {
    if (!text) return null;
    const fs = Math.max(24, size), padX = Math.round(fs * 0.55), padY = Math.round(fs * 0.30), rad = Math.round(fs * 0.22);
    const mCtx = document.createElement('canvas').getContext('2d');
    mCtx.font = `900 ${fs}px 'Arial Black', Impact, sans-serif`;
    const tw = Math.ceil(mCtx.measureText(text.toUpperCase()).width);
    const cw = tw + padX * 2, ch = fs + padY * 2;
    const cvs = document.createElement('canvas'); cvs.width = cw; cvs.height = ch;
    const ctx = cvs.getContext('2d'); ctx.clearRect(0, 0, cw, ch);
    ctx.fillStyle = '#fff'; ctx.beginPath();
    ctx.moveTo(rad, 0); ctx.lineTo(cw - rad, 0); ctx.quadraticCurveTo(cw, 0, cw, rad);
    ctx.lineTo(cw, ch - rad); ctx.quadraticCurveTo(cw, ch, cw - rad, ch);
    ctx.lineTo(rad, ch); ctx.quadraticCurveTo(0, ch, 0, ch - rad);
    ctx.lineTo(0, rad); ctx.quadraticCurveTo(0, 0, rad, 0);
    ctx.closePath(); ctx.fill();
    ctx.font = `900 ${fs}px 'Arial Black', Impact, sans-serif`;
    ctx.fillStyle = '#000'; ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
    ctx.fillText(text.toUpperCase(), cw / 2, ch / 2);
    return cvs.toDataURL('image/png');
  }, []);

  const startExport = useCallback(async () => {
    if (!jobId || !selectedLangs.length) return;
    setExporting(true); setStep(4);
    const init = {}; selectedLangs.forEach(c => { init[c] = { status: 'pending' }; }); setLangStatus(init);
    try {
      const voiceId = selVoice || selectedVoiceIds?.[0] || 'EXAVITQu4vr4xnSDxMaL';
      const allTranslations = {};
      selectedLangs.forEach(code => {
        const st = langState[code] || {}, hasT = st.hasTitle !== false && !!st.titleText;
        allTranslations[code] = { script: st.script || transcript, title: st.titleText || '', titlePng: hasT ? renderTitlePng(st.titleText, st.titleSize || 48) : null };
      });
      const firstSt = langState[selectedLangs[0]] || {};
      const _fs = firstSt.titleSize || 48;
      const tPxX = Math.round((firstSt.titlePos?.x || 50) / 100 * VIDEO_W);
      const tPxY = Math.round((firstSt.titlePos?.y || 18) / 100 * VIDEO_H);
      const maxLen = Math.max(...selectedLangs.map(c => (langState[c]?.titleText || '').length));
      await axios.post(`${base}/api/localize/generate`, {
        jobId, langs: selectedLangs, translations: allTranslations, voiceId, elApiKey, musicFilename,
        titlePxX: tPxX, titlePxY: tPxY, patchPxX: tPxX, patchPxY: tPxY,
        patchW: Math.min(VIDEO_W - 40, Math.round(maxLen * _fs * 0.58 + _fs * 1.1)),
        patchH: Math.round(_fs * 1.60),
        titleTimeIn, titleTimeOut,
        titleYPct: window._ocrTitleYPct || 18, captionYPct: window._ocrCaptionYPct || 88,
        voiceVolume: voVol / 100, musicVolume: muVol / 100,
      });
      const active = {}; selectedLangs.forEach(c => { active[c] = { status: 'generating_vo' }; });
      setLangStatus(prev => ({ ...prev, ...active }));
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      alert('Export failed: ' + msg);
      const errS = {}; selectedLangs.forEach(c => { errS[c] = { status: 'error', error: msg }; }); setLangStatus(errS);
    }
    setExporting(false);
  }, [jobId, selectedLangs, langState, transcript, selVoice, selectedVoiceIds, elApiKey, musicFilename, voVol, muVol, titleTimeIn, titleTimeOut, base, renderTitlePng]);

  const download = (code, filename) => {
    fetch(`${base}/api/stream/${filename}`).then(r => r.blob()).then(blob => {
      const blobUrl = URL.createObjectURL(blob), a = document.createElement('a');
      a.href = blobUrl; a.download = `localized_${code}.mp4`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    });
  };

  const playVideo = (filename) => {
    const bg = document.createElement('div');
    bg.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.8);z-index:9999;display:flex;align-items:center;justify-content:center;';
    const v = document.createElement('video'); v.src = `${base}/api/stream/${filename}`; v.controls = true; v.autoplay = true;
    v.style.cssText = 'max-width:90vw;max-height:90vh;border-radius:10px;';
    bg.appendChild(v); bg.onclick = e => { if (e.target === bg) { v.pause(); bg.remove(); } };
    document.body.appendChild(bg);
  };

  const resetAll = () => {
    clearInterval(pollRef.current); clearInterval(previewTimer.current);
    setStep(1); setJobId(null); setTranscript(''); setOcrTitle('');
    setSelectedLangs([]); setLangState({}); setLangStatus({});
    setMusicFilename(null); setVideoPreview(null); setOcrStatus('idle');
    setSttError(null); setPreviewPlaying(false); setPreviewProg(0);
    localStorage.removeItem('loc_jobId');
  };

  const shuffleMusic = () => {
    if (!musicBank.length) return;
    const cands = (musicBank.filter(m => m.mood === musicMood).filter(m => m.filename !== musicFilename));
    if (cands.length) setMusicFilename(cands[Math.floor(Math.random() * cands.length)].filename);
    else { const all = musicBank.filter(m => m.filename !== musicFilename); if (all.length) setMusicFilename(all[Math.floor(Math.random() * all.length)].filename); }
  };

  const musicLabel = musicBank.find(m => m.filename === musicFilename)?.label || musicFilename || '';
  const readyCount = Object.values(langStatus).filter(s => s?.status === 'done').length;
  const allReady   = readyCount === selectedLangs.length && readyCount > 0;
  const femaleVoices = locVoices.female.length ? locVoices.female : (voices || []).filter(v => v.gender === 'female' || v.category === 'premade_female').slice(0, 4);
  const maleVoices   = locVoices.male.length   ? locVoices.male   : (voices || []).filter(v => v.gender === 'male'   || v.category === 'premade_male').slice(0, 4);

  const inPct  = (titleTimeIn  / videoDur * 100).toFixed(2);
  const outPct = (titleTimeOut / videoDur * 100).toFixed(2);

  const stepState = (n) => {
    if (step > n) return 'step-done';
    if (step === n) return 'step-active';
    if (n === 2 && (!videoPreview || selectedLangs.length === 0)) return 'step-locked';
    if (n === 3 && step < 2) return 'step-locked';
    if (n === 4 && step < 3) return 'step-locked';
    return 'step-locked';
  };

  return (
    <div className="loc-page">

      {/* ── SIDEBAR ── */}
      <div className="loc-sidebar">
        <div
          className={`loc-dropzone-wrap${dragOver ? ' loc-dragover' : ''}${videoPreview ? ' loc-has-video' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !videoPreview && fileRef.current?.click()}
        >
          <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) handleUpload(e.target.files[0]); }} />
          {uploading ? (
            <div className="loc-uploading">
              <div className="loc-spinner">&#9203;</div>
              <div>Uploading + transcribing...</div>
            </div>
          ) : videoPreview ? (
            <div className="loc-video-v-wrap">
              <video src={videoPreview} className="loc-video-v" muted playsInline loop autoPlay />
              <button className="loc-change-btn" onClick={e => { e.stopPropagation(); fileRef.current?.click(); }}>Change</button>
            </div>
          ) : (
            <div className="loc-upload-placeholder">
              <div className="loc-upload-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 14.5V17a3 3 0 003 3h10a3 3 0 003-3v-2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M12 3v11M8.5 9.5L12 13l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p>Drag &amp; drop any file</p>
              <small>MP4 &middot; Max size: 500MB</small>
              <small>You can upload multiple files</small>
            </div>
          )}
        </div>

        {videoPreview && !uploading && (
          <div className="loc-upload-ok">
            &#10003; Transcribed
            {ocrStatus === 'found' && <span> &middot; OCR title detected</span>}
            {sttError && <span style={{ color: 'var(--accent)', marginLeft: 6 }}>&#9888; STT issue</span>}
          </div>
        )}
        {!elApiKey && <div className="loc-warn">Add ElevenLabs API key in Settings.</div>}
      </div>

      {/* ── BODY ── */}
      <div className="loc-body">

        {/* STEP 1 */}
        <div className={`loc-step-card ${stepState(1)}`}>
          <div className="loc-step-header">
            <span className="loc-step-num">{step > 1 ? '\u2713' : '1'}</span>
            <div>
              <div className="loc-step-label">Upload &amp; Languages</div>
              <div className="loc-step-desc">Select target languages to localize into</div>
            </div>
          </div>
          {step === 1 && (
            <div className="loc-step-content">
              <div className="loc-lang-grid">
                {ALL_LANGS.map(l => (
                  <button key={l.code} className={`loc-lang-btn ${selectedLangs.includes(l.code) ? 'active' : ''}`}
                    onClick={() => setSelectedLangs(p => p.includes(l.code) ? p.filter(c => c !== l.code) : [...p, l.code])}>
                    <span className="loc-lang-flag">{l.flag}</span><span>{l.code}</span>
                  </button>
                ))}
              </div>
              {selectedLangs.length > 0 && videoPreview && !uploading && (
                <button className="loc-btn loc-btn-primary" style={{ alignSelf: 'flex-end' }} onClick={goToStep2}>
                  Script + Title ({selectedLangs.length} lang{selectedLangs.length > 1 ? 's' : ''}) &#8594;
                </button>
              )}
              {!videoPreview && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>&#8592; Upload a video first</div>
              )}
            </div>
          )}
          {step > 1 && (
            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', paddingTop: 4 }}>
              {selectedLangs.join(' · ')} &mdash; <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.72rem', padding: 0 }}>Edit</button>
            </div>
          )}
        </div>

        {/* STEP 2 */}
        <div className={`loc-step-card ${stepState(2)}`}>
          <div className="loc-step-header">
            <span className="loc-step-num">{step > 2 ? '\u2713' : '2'}</span>
            <div>
              <div className="loc-step-label">Script + Title</div>
              <div className="loc-step-desc">Edit translated scripts and title timing</div>
            </div>
            {step === 2 && translating && (
              <span style={{ fontSize: 10, color: 'var(--accent)', marginLeft: 'auto', fontFamily: 'DM Sans, sans-serif' }}>Translating...</span>
            )}
          </div>
          {step === 2 && (
            <div className="loc-step-content">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'DM Sans, sans-serif' }}>Title timing</span>
                <span style={{ fontSize: '0.68rem', color: 'var(--accent)', fontFamily: 'DM Sans, sans-serif', fontWeight: 700 }}>
                  {titleTimeIn.toFixed(1)}s &#8594; {titleTimeOut >= videoDur ? 'end' : titleTimeOut.toFixed(1) + 's'}
                </span>
                {titleTimeOutAutoDetected && <span style={{ fontSize: 9, background: 'rgba(26,138,58,.1)', color: 'var(--success)', padding: '1px 6px', borderRadius: 3, fontWeight: 600 }}>&#10003; auto</span>}
              </div>
              <div
                ref={timelineRef}
                style={{ position: 'relative', height: 36, background: 'var(--surface2)', borderRadius: 6, marginBottom: 16, cursor: 'crosshair', userSelect: 'none' }}
                onMouseDown={e => { const t = getTimeFromEvent(e); if (t === null) return; const dIn = Math.abs(t - titleTimeIn), dOut = Math.abs(t - titleTimeOut); if (dIn < dOut) setTitleTimeIn(Math.min(t, titleTimeOut - 0.1)); else setTitleTimeOut(Math.max(t, titleTimeIn + 0.1)); }}>
                {Array.from({ length: Math.min(videoDur + 1, 11) }, (_, i) => {
                  const t = Math.round(i / Math.min(videoDur, 10) * videoDur * 10) / 10;
                  return <div key={i} style={{ position: 'absolute', left: `${t / videoDur * 100}%`, top: 0, bottom: 0, borderLeft: '0.5px solid var(--border)', pointerEvents: 'none' }}><span style={{ position: 'absolute', bottom: -14, left: 2, fontSize: 9, color: 'var(--text-dim)' }}>{t.toFixed(1)}s</span></div>;
                })}
                <div style={{ position: 'absolute', top: 4, bottom: 4, background: 'var(--accent)', opacity: .18, borderRadius: 4, left: `${inPct}%`, right: `${(100 - parseFloat(outPct)).toFixed(2)}%`, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: '50%', left: `${inPct}%`, transform: 'translate(-50%,-50%)', width: 16, height: 28, background: 'var(--success)', borderRadius: 4, cursor: 'ew-resize', zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => startTimelineDrag(e, 'in')}>
                  <div style={{ width: 2, height: 12, background: 'rgba(255,255,255,.8)', borderRadius: 1 }} />
                </div>
                <div style={{ position: 'absolute', top: '50%', left: `${outPct}%`, transform: 'translate(-50%,-50%)', width: 16, height: 28, background: 'var(--danger)', borderRadius: 4, cursor: 'ew-resize', zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => startTimelineDrag(e, 'out')}>
                  <div style={{ width: 2, height: 12, background: 'rgba(255,255,255,.8)', borderRadius: 1 }} />
                </div>
              </div>

              <div className="loc-lang-grid-2col">
                {selectedLangs.map(code => {
                  const lang = ALL_LANGS.find(l => l.code === code);
                  const st   = langState[code] || defaultLangState(ocrTitle);
                  return (
                    <div key={code} className="loc-card">
                      <div className="loc-lang-card-hd">
                        <span style={{ fontSize: 18 }}>{lang?.flag}</span>
                        <span className="loc-lang-card-name">{lang?.name}</span>
                        {translating && !st.script && <span className="loc-ocr-badge scanning">&#8999;</span>}
                        <label className="loc-title-toggle">
                          <input type="checkbox" checked={st.hasTitle !== false} onChange={e => updateLang(code, { hasTitle: e.target.checked })} />
                          On-screen title
                        </label>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: `${CANVAS_W}px 1fr`, gap: 12, alignItems: 'start' }}>
                        <canvas ref={el => { canvasRefs.current[code] = el; if (el) drawCanvas(code); }} width={CANVAS_W} height={CANVAS_H}
                          style={{ borderRadius: 8, flexShrink: 0, display: 'block', cursor: st.hasTitle ? 'ns-resize' : 'default' }}
                          onMouseDown={st.hasTitle ? (e => {
                            e.preventDefault();
                            const cvs = canvasRefs.current[code]; if (!cvs) return;
                            const r = cvs.getBoundingClientRect();
                            const startX = e.clientX, startXPct = st.titlePos.x;
                            const startY = e.clientY, startPct = st.titlePos.y;
                            const onMove = ev => updateLang(code, { titlePos: { x: Math.min(95, Math.max(5, startXPct + ((ev.clientX - startX) / r.width) * 100)), y: Math.min(90, Math.max(5, startPct + ((ev.clientY - startY) / r.height) * 100)) } });
                            const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                            window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
                          }) : undefined} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                          {st.hasTitle !== false ? (
                            <>
                              <div className="loc-script-lbl">Title {code}</div>
                              <input className="loc-title-input" type="text" value={st.titleText} onChange={e => updateLang(code, { titleText: e.target.value })} placeholder="Translated title" />
                              <div className="loc-ctrl-hint">White bg auto-width &middot; drag canvas to reposition</div>
                              <div className="loc-ctrl-row">
                                <span className="loc-ctrl-lbl">Size</span>
                                <input type="range" min={24} max={120} step={2} value={st.titleSize} onChange={e => updateLang(code, { titleSize: +e.target.value })} />
                                <span className="loc-ctrl-val">{Math.round(st.titleSize)}px</span>
                              </div>
                            </>
                          ) : (
                            <div style={{ fontSize: 11, color: 'var(--text-dim)', padding: 8, background: 'var(--surface)', borderRadius: 6 }}>No title for this language</div>
                          )}
                          <div className="loc-script-lbl" style={{ marginTop: 6 }}>Script {code}</div>
                          <textarea className="loc-textarea" rows={4} value={st.script} onChange={e => updateLang(code, { script: e.target.value })} placeholder={translating ? 'Translating...' : 'Translated voiceover script'} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="loc-footer">
                <button className="loc-btn" onClick={() => setStep(1)}>&#8592; Back</button>
                {!translating && !Object.values(langState).some(s => s.script) && (
                  <button className="loc-btn" onClick={() => runTranslate(selectedLangs, transcript, ocrTitle)}>Generate translations</button>
                )}
                <button className="loc-btn loc-btn-primary" onClick={() => setStep(3)}>Voice + Music &#8594;</button>
              </div>
            </div>
          )}
        </div>

        {/* STEP 3 */}
        <div className={`loc-step-card ${stepState(3)}`}>
          <div className="loc-step-header">
            <span className="loc-step-num">{step > 3 ? '\u2713' : '3'}</span>
            <div>
              <div className="loc-step-label">Voice + Music</div>
              <div className="loc-step-desc">Pick voice, music &amp; mix levels</div>
            </div>
          </div>
          {step === 3 && (
            <div className="loc-step-content">
              <div className="loc-card">
                <div className="loc-label">AI Voice</div>
                {(voices || []).length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>Add ElevenLabs API key and sync voices in Settings.</div>
                ) : (
                  <div className="loc-voice-cols">
                    <div>
                      <div className="loc-voice-group-label female">Female</div>
                      <div className="loc-voice-grid">
                        {femaleVoices.map(v => (
                          <div key={v.id} className={`loc-voice-card ${selVoice === v.id ? 'active' : ''}`} onClick={() => setSelVoice(v.id)}>
                            <div className="loc-voice-name">{v.label}</div>
                            {v.accent && <div className="loc-voice-id">{v.accent}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="loc-voice-group-label male">Male</div>
                      <div className="loc-voice-grid">
                        {maleVoices.map(v => (
                          <div key={v.id} className={`loc-voice-card ${selVoice === v.id ? 'active' : ''}`} onClick={() => setSelVoice(v.id)}>
                            <div className="loc-voice-name">{v.label}</div>
                            {v.accent && <div className="loc-voice-id">{v.accent}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="loc-two-col">
                <div className="loc-card">
                  <div className="loc-label">Music{musicMood && <span className="loc-mood-tag">{musicMood}</span>}</div>
                  {musicFilename ? (
                    <div className="loc-music-row">
                      <span className="loc-music-label">&#10003; {musicLabel}</span>
                      <button className="loc-btn" onClick={shuffleMusic}>&#8635;</button>
                      <button className="loc-btn" onClick={() => setMusicFilename(null)}>&#10005;</button>
                      <button className="loc-btn" onClick={() => setMusicBrowser(b => !b)}>{musicBrowser ? 'Close' : 'Library'}</button>
                    </div>
                  ) : (
                    <div className="loc-music-empty"><span>No music selected</span>{musicBank.length > 0 && <button className="loc-btn" onClick={() => setMusicBrowser(true)}>Library</button>}</div>
                  )}
                  {musicBrowser && (
                    <div className="loc-music-list">
                      {musicBank.map(m => (
                        <div key={m.filename} className={`loc-music-item ${musicFilename === m.filename ? 'active' : ''}`} onClick={() => { setMusicFilename(m.filename); setMusicBrowser(false); }}>
                          &#9834; {m.label || m.filename}{m.mood && <span className="loc-mood-tag" style={{ marginLeft: 4 }}>{m.mood}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="loc-card">
                  <div className="loc-label">Audio mix</div>
                  <div className="loc-mix-rows">
                    {[['VO', voVol, setVoVol, 'var(--accent)'], ['Music', muVol, setMuVol, '#378ADD']].map(([lb, val, setter, col]) => (
                      <div key={lb} className="loc-mix-row">
                        <span className="loc-mix-lbl">{lb}</span>
                        <input type="range" min={0} max={100} step={5} value={val} onChange={e => setter(+e.target.value)} style={{ flex: 1, accentColor: col }} />
                        <span className="loc-ctrl-val" style={{ width: 34 }}>{val}%</span>
                        <div className="loc-mix-bar"><div className="loc-mix-fill" style={{ width: `${val}%`, background: col }} /></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="loc-card">
                <div className="loc-label">10s audio preview<span className="loc-badge" style={{ background: 'rgba(55,138,221,.1)', color: '#185FA5' }}>before generating</span></div>
                <div className="loc-preview-wrap">
                  <div className="loc-preview-top">
                    <button className={`loc-btn ${previewPlaying ? 'loc-btn-primary' : ''}`} style={{ minWidth: 60 }} onClick={togglePreview} disabled={previewLoading}>
                      {previewLoading ? '&#9203;' : previewPlaying ? '&#9208; Stop' : '&#9654; Play'}
                    </button>
                    <span className="loc-preview-info">
                      {selVoice ? (voices || []).find(v => v.id === selVoice)?.label || 'Selected voice' : 'No voice selected'}
                      {' \u00b7 '}{musicLabel || 'no music'}{' \u00b7 VO '}{voVol}%{' \u00b7 Music '}{muVol}%
                    </span>
                    <span className="loc-preview-time">{previewPlaying ? Math.round(previewProg * 10) : 0}s / 10s</span>
                  </div>
                  <div className="loc-waves">{Array.from({ length: 36 }, (_, i) => <div key={i} className="loc-wave-bar" style={{ height: previewPlaying ? `${4 + Math.abs(Math.sin(Date.now() / 200 + i * 0.45)) * 22}px` : '4px', opacity: previewPlaying ? 1 : 0.25 }} />)}</div>
                  <div className="loc-preview-progress"><div style={{ width: `${previewProg * 100}%`, height: '100%', background: 'var(--accent)', borderRadius: 2, transition: 'width .2s' }} /></div>
                </div>
              </div>

              <div className="loc-footer">
                <button className="loc-btn" onClick={() => { clearInterval(previewTimer.current); setPreviewPlaying(false); setPreviewProg(0); setStep(2); }}>&#8592; Back</button>
                <button className="loc-btn loc-btn-primary" disabled={exporting} onClick={startExport}>
                  {exporting ? 'Starting...' : `Generate ${selectedLangs.length} version${selectedLangs.length !== 1 ? 's' : ''} \u2192`}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* STEP 4 */}
        <div className={`loc-step-card ${stepState(4)}`}>
          <div className="loc-step-header">
            <span className="loc-step-num">{step > 4 ? '\u2713' : '4'}</span>
            <div>
              <div className="loc-step-label">Export</div>
              <div className="loc-step-desc">{step === 4 ? `${readyCount} / ${selectedLangs.length} ready` : 'Download localized versions'}</div>
            </div>
            {step === 4 && allReady && (
              <button className="loc-btn loc-btn-primary" style={{ marginLeft: 'auto' }}
                onClick={() => Object.entries(langStatus).forEach(([c, s]) => { if (s?.status === 'done' && s.filename) download(c, s.filename); })}>
                &#11015; Download all
              </button>
            )}
          </div>
          {step === 4 && (
            <div className="loc-step-content">
              <div className="loc-export-grid">
                {selectedLangs.map(code => {
                  const lang = ALL_LANGS.find(l => l.code === code);
                  const s    = langStatus[code] || { status: 'pending' };
                  const st   = langState[code];
                  const pct  = s.status === 'done' ? 100 : s.status === 'generating_vo' ? 40 : s.status === 'rendering' ? 70 : 8;
                  return (
                    <div key={code} className={`loc-export-card ${s.status === 'done' ? 'ready' : ''}`}>
                      <div className="loc-card-header">
                        <span className="loc-card-flag">{lang?.flag}</span>
                        <span className="loc-card-name">{lang?.name || code}</span>
                        {s.status === 'done'
                          ? <span style={{ fontSize: 10, color: 'var(--success)', marginLeft: 'auto' }}>&#10003; Ready</span>
                          : <span style={{ fontSize: 10, color: 'var(--accent)', marginLeft: 'auto' }}>Rendering...</span>}
                      </div>
                      <div className="loc-thumb">
                        {s.status === 'done' && s.filename
                          ? <video src={`${base}/api/stream/${s.filename}`} className="loc-thumb-video" muted playsInline onMouseOver={e => e.currentTarget.play()} onMouseOut={e => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }} />
                          : <div className="loc-thumb-dark"><div className="loc-thumb-spinner">&#9203;</div></div>
                        }
                        {st?.titleText && st?.hasTitle !== false && <div className="loc-thumb-title-impact">{st.titleText}</div>}
                        <div className="loc-thumb-progress"><div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)', transition: 'width .4s' }} /></div>
                      </div>
                      {s.status === 'done' && s.filename && (
                        <div className="loc-card-actions">
                          <button className="loc-btn" style={{ flex: 1 }} onClick={() => playVideo(s.filename)}>&#9654; Play</button>
                          <button className="loc-btn loc-btn-primary" style={{ flex: 1 }} onClick={() => download(code, s.filename)}>&#11015; Download</button>
                        </div>
                      )}
                      {s.status === 'error' && <div className="loc-card-error">{s.error || 'Failed'}</div>}
                    </div>
                  );
                })}
              </div>
              <div className="loc-footer" style={{ marginTop: 8 }}>
                <button className="loc-btn" onClick={resetAll}>&#10005; New localization</button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
